import depsloader from "../../../depsloader";

const GEOCODER_SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const GEOCODER_REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

const KNOWN_TYPES = ["house", "street", "district", "city", "locality", "county", "state", "country", "other"];

// Runtime config is printed by the theme per request (Yeti\MapsRuntime).
// The maps key expires, so it is read on every request and never bundled.
function mapsRuntime() {
  if (typeof window === "undefined" || !window.ScreenworkMapsRuntime) {
    return {};
  }

  return window.ScreenworkMapsRuntime;
}

function geocoderKey() {
  const key = mapsRuntime().geocoderKey;
  return typeof key === "string" && key.trim() !== "" ? key : "";
}

function geocoderError(message, flags) {
  const error = new Error(message);
  Object.keys(flags || {}).forEach(k => {
    error[k] = flags[k];
  });
  return error;
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function toCoordinate(v) {
  if (typeof v === "string" && v.trim() !== "") {
    v = Number(v);
  }

  return isFiniteNumber(v) ? v : null;
}

function str(v) {
  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }

  return typeof v === "string" && v.trim() !== "" ? v : null;
}

// Fallback for responses without properties.type (legacy osm_key/osm_value).
function legacyType(p) {
  if (p.osm_key == "highway") {
    return "street";
  }
  if (p.osm_key == "boundary") {
    return "district";
  }
  if (p.osm_key == "place") {
    if (p.osm_value == "suburb" || p.osm_value == "quarter" || p.osm_value == "neighbourhood") {
      return "district";
    }
    if (["city", "town", "village", "hamlet", "municipality"].indexOf(p.osm_value) > -1) {
      return "city";
    }
    if (p.osm_value == "house") {
      return "house";
    }
  }
  if (str(p.housenumber)) {
    return "house";
  }

  return "other";
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  hasKey() {
    return geocoderKey() !== "";
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.label) {
      p.push(g.label);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && g.state != g.label) {
      p.push(g.state);
    }

    if (g.country && g.country != g.label) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Maps a GeoJSON feature to the address object the components expect
  // (street, number, zip, city, district, state, country, lat, lng, text).
  // The feature itself is never modified.
  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    // GeoJSON order is [lng, lat]
    const lng = toCoordinate(l.geometry.coordinates[0]);
    const lat = toCoordinate(l.geometry.coordinates[1]);
    if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }

    const p = l.properties && typeof l.properties === "object" ? l.properties : {};
    const type = KNOWN_TYPES.indexOf(p.type) > -1 ? p.type : legacyType(p);

    const name = str(p.name);
    const geoaddress = {};
    let street = str(p.street);
    let city = str(p.city);
    let district = str(p.district);
    let label = null;

    switch (type) {
      case "house":
        // a building/POI name is a label, not a street
        if (name && name != street) {
          label = name;
        }
        break;

      case "street":
        if (!street) {
          street = name;
        } else if (name && name != street) {
          label = name;
        }
        break;

      case "district":
        if (name && name != city) {
          district = name;
        } else if (!city) {
          label = name;
        }
        break;

      case "city":
        city = name || city;
        break;

      default:
        // locality, county, state, country, other: keep the name as label,
        // never invent it as city.
        if (name && name != city && name != district) {
          label = name;
        }
        break;
    }

    if (street) {
      geoaddress["street"] = street;
      if (str(p.housenumber)) {
        geoaddress["number"] = str(p.housenumber);
      }
    }

    if (str(p.postcode)) {
      geoaddress["zip"] = str(p.postcode);
    }
    if (city) {
      geoaddress["city"] = city;
    }
    if (district && district != city) {
      geoaddress["district"] = district;
    }
    if (label) {
      geoaddress["label"] = label;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = str(p.state) || undefined;
    geoaddress["country"] = str(p.country) || undefined;
    geoaddress["type"] = type;

    geoaddress["text"] = this._geoaddressText(geoaddress);

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  _features(res) {
    if (res && res.data && Array.isArray(res.data.features)) {
      return res.data.features;
    }

    throw geocoderError("invalid geocoder response");
  }

  _rejectRequestError(thrown, reject) {
    if (this.axios.isCancel(thrown)) {
      reject(geocoderError("geocoder request cancelled", { cancelled: true }));
      return;
    }

    reject(thrown);
  }

  geocode(params) {
    return new Promise((resolve, reject) => {
      const key = geocoderKey();
      if (!key) {
        reject(geocoderError("maps key missing", { missingKey: true }));
        return;
      }

      params.set("key", key);

      const CancelToken = this.axios.CancelToken;
      const request = {
        source: CancelToken.source()
      };
      this.axiosRequest = request;

      this.axios.get(GEOCODER_SEARCH_URL, {
        cancelToken: request.source.token,
        timeout: REQUEST_TIMEOUT,
        params
      }).then(res => {
        const results = [];

        this._features(res).forEach(l => {
          const geoaddress = this._geoaddress(l);
          if (geoaddress) {
            results.push(geoaddress);
          }
        });

        resolve(results);
      }).catch(thrown => {
        this._rejectRequestError(thrown, reject);
      }).finally(() => {
        // only reset if no newer request has replaced this one
        if (this.axiosRequest === request) {
          this.axiosRequest = null;
        }
      });
    });
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    //params.append("osm_tag", "!boundary:protected_area");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    return new Promise((resolve, reject) => {
      lat = toCoordinate(lat);
      lng = toCoordinate(lng);

      if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        reject(geocoderError("invalid coordinates"));
        return;
      }

      const key = geocoderKey();
      if (!key) {
        reject(geocoderError("maps key missing", { missingKey: true }));
        return;
      }

      const params = new URLSearchParams();
      params.append("lat", lat);
      params.append("lon", lng);
      params.append("lang", "de");
      params.append("key", key);

      this.axios.get(GEOCODER_REVERSE_URL, {
        timeout: REQUEST_TIMEOUT,
        params
      }).then(res => {
        const features = this._features(res);
        const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;

        if (geoaddress) {
          resolve(geoaddress);
        } else {
          reject(geocoderError("invalid data"));
        }
      }).catch(err => {
        this._rejectRequestError(err, reject);
      });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
